import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const QuickInputDialog = ({
  open,
  title,
  label,
  value,
  type = "text",
  onClose,
  onConfirm,
  isLoading = false,
  // Props opcionales para selector de prefijo móvil
  showPrefixSelector = false,
  mobilePrefixes = [],
  selectedPrefix = null,
  onPrefixChange = null,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setInputValue(value || '');
    setError('');
  }, [value, open]);

  // Función para validar email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Función para validar el input
  const validateInput = (value) => {
    if (!value.trim()) {
      return t('common.fieldRequired');
    }
    
    if (type === 'email' && !validateEmail(value)) {
      return t('common.invalidEmail');
    }
    
    return '';
  };

  const handleConfirm = () => {
    const validationError = validateInput(inputValue);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setError('');
    onConfirm(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {showPrefixSelector && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete
                sx={{ width: '200px' }}
                options={mobilePrefixes}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  return option.mobile_prefix 
                    ? `+${option.mobile_prefix} (${option.country})` 
                    : option.country;
                }}
                value={selectedPrefix || null}
                onChange={(e, newValue) => onPrefixChange && onPrefixChange(newValue)}
                disableClearable={true}
                disabled={isLoading}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={t('common.prefix')} 
                    margin="none"
                  />
                )}
              />
              <TextField
                label={label}
                type={type}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  // Limpiar error cuando el usuario empieza a escribir
                  if (error) setError('');
                }}
                fullWidth
                autoFocus
                disabled={isLoading}
                onKeyDown={handleKeyDown}
                variant="outlined"
                error={!!error}
                helperText={error}
              />
            </Box>
          )}
          {!showPrefixSelector && (
            <TextField
              label={label}
              type={type}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                // Limpiar error cuando el usuario empieza a escribir
                if (error) setError('');
              }}
              fullWidth
              autoFocus
              disabled={isLoading}
              onKeyDown={handleKeyDown}
              variant="outlined"
              error={!!error}
              helperText={error}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained" 
          color="primary"
          disabled={isLoading || !inputValue.trim() || !!error}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickInputDialog;
